import React, { useMemo, useRef } from "react";
import clsx from "clsx";

import {
  HvFormElement,
  HvLabel,
  HvInfoMessage,
  HvWarningText,
  HvListContainer,
  useControlled,
  HvFormElementProps,
  HvTag,
  HvListItem,
  HvTypography,
} from "@hitachivantara/uikit-react-core";

import { setId, useUniqueId } from "lib/utils/ids";

import useStyles from "./styles";

export interface HvTagListValue<T> {
  id: string;
  label: string | React.ReactNode;
  color?: string;
  value: T;
}

export interface HvTagListProps<T>
  extends Omit<HvFormElementProps, "value" | "defaultValue" | "onChange"> {
  value?: Array<HvTagListValue<T>>;
  defaultValue?: Array<HvTagListValue<T>>;
  onChange?: (value: Array<HvTagListValue<T>>) => void;
}

const HvTagList = <T extends unknown>(
  props: HvTagListProps<T>
): JSX.Element => {
  const {
    id,
    name,
    value: valueProp,
    defaultValue,

    required = false,
    readOnly = false,
    disabled = false,

    label,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    description,
    "aria-describedby": ariaDescribedBy,

    onChange,

    status,
    statusMessage,
    "aria-errormessage": ariaErrorMessage,

    ...others
  } = props;

  const classes = useStyles();

  const elementId = useUniqueId(id, "hvtaglist");

  const [value, setValue] = useControlled(valueProp, defaultValue);

  const [validationState, setValidationState] = useControlled(
    status,
    "standBy"
  );

  const [validationMessage] = useControlled(statusMessage, "Required");

  const listContainer = useRef<HTMLElement>();

  const items = useMemo(() => {
    return (value as Array<HvTagListValue<T>>)?.map((child, i) => (
      <HvListItem key={child.id} className={classes.listItem}>
        <HvTag
          className={classes.tag}
          type="semantic"
          label={
            <HvTypography variant="normalText">{child.label}</HvTypography>
          }
          color={child.color}
          onDelete={() => {
            const newValue = [...value.slice(0, i), ...value.slice(i + 1)];

            onChange?.(newValue);

            listContainer.current?.focus();

            setValue(() => {
              // this will only run if uncontrolled

              if (required && newValue.length === 0) {
                setValidationState("invalid");
              } else {
                setValidationState("valid");
              }

              return newValue;
            });
          }}
          disabled={disabled}
        />
      </HvListItem>
    ));
  }, [
    classes.listItem,
    classes.tag,
    disabled,
    onChange,
    required,
    setValidationState,
    setValue,
    value,
  ]);

  // the error message area will only be created if:
  // - an external element that provides an error message isn't identified via aria-errormessage AND
  //   - both status and statusMessage properties are being controlled OR
  //   - status is uncontrolled and required is true
  const canShowError =
    ariaErrorMessage == null &&
    ((status !== undefined && statusMessage !== undefined) ||
      (status === undefined && required));

  const errorMessageId = canShowError
    ? setId(elementId, "error")
    : ariaErrorMessage;

  const hasLabel = label != null;
  const hasDescription = description != null;

  return (
    <HvFormElement
      id={id}
      name={name}
      status={validationState}
      disabled={disabled}
      required={required}
      readOnly={readOnly}
      className={classes.root}
    >
      {label && (
        <HvLabel
          id={setId(elementId, "label")}
          label={label}
          className={classes.label}
        />
      )}
      {hasDescription && (
        <HvInfoMessage
          id={setId(elementId, "description")}
          className={classes.description}
        >
          {description}
        </HvInfoMessage>
      )}

      <HvListContainer
        id={hasLabel ? setId(elementId, "list") : undefined}
        condensed
        role="list"
        aria-label={ariaLabel}
        aria-labelledby={
          [hasLabel && setId(elementId, "label"), ariaLabelledBy]
            .join(" ")
            .trim() || undefined
        }
        aria-invalid={validationState === "invalid" ? true : undefined}
        aria-errormessage={
          validationState === "invalid" ? errorMessageId : undefined
        }
        aria-describedby={
          [hasDescription && setId(elementId, "description"), ariaDescribedBy]
            .join(" ")
            .trim() || undefined
        }
        className={clsx(classes.list, {
          [classes.disabled]: disabled,
          [classes.invalid]: validationState === "invalid",
          [classes.canShowError]: canShowError,
        })}
        ref={listContainer}
        tabIndex={-1}
        {...others}
      >
        {items}
      </HvListContainer>

      {canShowError && (
        <HvWarningText
          id={setId(elementId, "error")}
          disableBorder
          className={classes.error}
        >
          {validationMessage}
        </HvWarningText>
      )}
    </HvFormElement>
  );
};

export default HvTagList;
